import { AddCircle as AddCircleIcon } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import type { TestContext, TestRunStep } from "../../../types";
import { getStepGroupMetadata, isContextVisible } from "../../../utils/group-utils";
import { TestStepCard } from "./test-step-card";
import { TestStepsFloatingActionBar } from "./test-steps-floating-action-bar";

interface TestStepsPanelProps {
  ctxs: TestContext[] | undefined;
  selectedCtx: TestContext | null;
  onContextSelect: (ctx: TestContext) => void;
  onContextCreate: (context: Partial<TestContext>) => void;
  onContextMove: (contextId: string, newOrder: number) => void;
  onContextDelete: (contextId: string) => void;
  onContextDuplicate: (contextId: string) => void;
  onBulkDuplicate: (contexts: TestContext[]) => Promise<void>;
  onBulkDelete: (contexts: TestContext[]) => Promise<void>;
  onBulkToggleDisable: (contexts: TestContext[]) => Promise<void>;
  onContextToggleDisable: (contextId: string) => void;
  isCreating: boolean;
  isMoving: boolean;
  mutating: boolean;
  getStepInfo: (ctx: TestContext) => TestRunStep | null;
}

export function TestStepsPanel({
  ctxs,
  selectedCtx,
  onContextSelect,
  onContextCreate,
  onContextMove,
  onContextDelete,
  onContextDuplicate,
  onBulkDuplicate,
  onBulkDelete,
  onBulkToggleDisable,
  onContextToggleDisable,
  isCreating,
  mutating,
  getStepInfo,
}: TestStepsPanelProps) {
  const [selectedContexts, setSelectedContexts] = useState<TestContext[]>([]);
  const [draggedContextId, setDraggedContextId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [insertBeforeIndex, setInsertBeforeIndex] = useState<number | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const handleToggleSelection = useCallback((ctx: TestContext, checked: boolean) => {
    if (checked) {
      setSelectedContexts((prev) => [...prev, ctx]);
    } else {
      setSelectedContexts((prev) => prev.filter((c) => c.id !== ctx.id));
    }
  }, []);

  const handleDeleteSelected = async (contexts: TestContext[]) => {
    if (contexts.length === 0) return;
    await onBulkDelete(contexts);
    setSelectedContexts([]);
  };

  const handleDuplicateSelected = async (contexts: TestContext[]) => {
    if (contexts.length === 0) return;
    await onBulkDuplicate(contexts);
    setSelectedContexts([]);
  };

  const handleToggleDisableSelected = async (contexts: TestContext[]) => {
    if (contexts.length === 0) return;
    await onBulkToggleDisable(contexts);
    setSelectedContexts([]);
  };

  const handleToggleGroupCollapse = useCallback((groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  const handleDragStart = useCallback((contextId: string) => {
    setDraggedContextId(contextId);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);

      if (!draggedContextId || !ctxs) return;

      const draggedIndex = ctxs.findIndex((ctx) => ctx.id === draggedContextId);
      if (draggedIndex === -1) return;

      // Determine insertion position based on where we're dragging
      // Always insert before the target index (the item will be placed at that position)
      let insertPos = index;

      // Special case: if dragging over the last item and we're not already at the end,
      // allow inserting after it
      const isLastItem = index === ctxs.length - 1;
      if (isLastItem && draggedIndex !== index) {
        // Check mouse Y position to determine if inserting before or after
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const mouseY = e.clientY;
        const itemCenter = rect.top + rect.height / 2;

        if (mouseY > itemCenter && draggedIndex < index) {
          // Insert after last item
          insertPos = ctxs.length;
        } else {
          // Insert before last item
          insertPos = index;
        }
      } else {
        // For all other cases, insert before the target index
        insertPos = index;
      }

      setInsertBeforeIndex(insertPos);
    },
    [draggedContextId, ctxs],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
    setInsertBeforeIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();

      // Use the insertion index we calculated during drag over
      const finalInsertIndex = insertBeforeIndex !== null ? insertBeforeIndex : dropIndex;

      setDragOverIndex(null);
      setInsertBeforeIndex(null);

      if (!draggedContextId || !ctxs) return;

      const draggedIndex = ctxs.findIndex((ctx) => ctx.id === draggedContextId);
      if (draggedIndex === -1) {
        setDraggedContextId(null);
        return;
      }

      // If inserting at the same position, do nothing
      if (
        finalInsertIndex === draggedIndex ||
        (finalInsertIndex === draggedIndex + 1 && draggedIndex < ctxs.length - 1)
      ) {
        setDraggedContextId(null);
        return;
      }

      // Backend removes the item first, then inserts at new_order position
      // So we need to calculate the target position after removal
      let newOrder = finalInsertIndex;
      if (draggedIndex < finalInsertIndex) {
        // Dragging forward: the list will be shorter, so subtract 1
        newOrder = finalInsertIndex - 1;
      }
      // Cap at max valid index
      const maxIndex = ctxs.length - 1;
      newOrder = Math.min(newOrder, maxIndex);
      newOrder = Math.max(newOrder, 0);

      onContextMove(draggedContextId, newOrder);
      setDraggedContextId(null);
    },
    [draggedContextId, ctxs, insertBeforeIndex, onContextMove],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedContextId(null);
    setDragOverIndex(null);
    setInsertBeforeIndex(null);
  }, []);

  const handleCreateNewStep = () => {
    const now = new Date();
    const blockIx = ctxs?.length || 0;
    onContextCreate({
      name: "<some name>",
      description: "<some description>",
      start_timestamp: blockIx + 1,
      end_timestamp: blockIx + 2,
      started_at: now.toISOString(),
      ended_at: now.toISOString(),
      end_goal: "<some end goal>",
      disabled: true,
      data: {
        id: blockIx + 1,
        name: "<some name>",
        description: "<some description>",
        end_goal: "<some end goal>",
        is_async: false,
        should_fix_with_ai: false,
        logs: [],
        should_try_deterministic: false,
        should_skip: true,
        fixed: false,
        skipped: false,
        is_flaky: false,
        code: "# No code yet",
        locator: {
          chain: [],
        },
        action: {
          action: "ai_action",
          value: "Do nothing",
        },
      },
    });
  };

  const ctxsList = useMemo(() => {
    if (!ctxs || ctxs.length === 0) return [];

    // Filter to visible contexts only (accounting for collapsed groups)
    const visibleContexts = ctxs.filter((ctx) => isContextVisible(ctx, ctxs, collapsedGroups));

    return visibleContexts.map((ctx) => {
      // Get the actual index in the full ctxs array
      const actualIndex = ctxs.findIndex((c) => c.id === ctx.id);

      const isSelected = selectedCtx?.id === ctx.id;
      const maybeStep = getStepInfo(ctx);
      const blockIx = ctx.data?.id || actualIndex;

      // Get group metadata for this step
      const groupMetadata = getStepGroupMetadata(ctx, ctxs);
      const isGroupCollapsed = groupMetadata.groupName
        ? collapsedGroups.has(groupMetadata.groupName)
        : false;

      const isChecked = selectedContexts.some((c) => c.id === ctx.id);
      const isDragging = draggedContextId === ctx.id;
      const isDragOver = dragOverIndex === actualIndex && draggedContextId !== ctx.id;
      // Don't show line before first item if we're already showing it at the top
      const showInsertLineBefore = insertBeforeIndex === actualIndex && !(insertBeforeIndex === 0);

      return (
        <React.Fragment key={`ctx-${ctx.id}`}>
          {showInsertLineBefore && (
            <Box
              sx={{
                height: "2px",
                backgroundColor: "primary.main",
                borderRadius: "1px",
                margin: "24px 0",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "-4px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                },
              }}
            />
          )}
          <TestStepCard
            ctx={ctx}
            isSelected={isSelected}
            isInSelectionMode={selectedContexts.length > 0}
            isChecked={isChecked}
            stepNumber={blockIx + 1}
            stepInfo={maybeStep}
            isDragging={isDragging}
            isDragOver={isDragOver}
            isDragActive={draggedContextId !== null}
            groupMetadata={groupMetadata}
            isGroupCollapsed={isGroupCollapsed}
            onSelect={onContextSelect}
            onToggleSelection={handleToggleSelection}
            onDelete={(ctx) => onContextDelete(ctx.id)}
            onDuplicate={(ctx) => onContextDuplicate(ctx.id)}
            onToggleDisable={(ctx) => onContextToggleDisable(ctx.id)}
            onToggleGroupCollapse={handleToggleGroupCollapse}
            onDragStart={() => handleDragStart(ctx.id)}
            onDragOver={(e) => handleDragOver(e, actualIndex)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, actualIndex)}
            onDragEnd={handleDragEnd}
          />
        </React.Fragment>
      );
    });
  }, [
    ctxs,
    selectedCtx,
    selectedContexts,
    draggedContextId,
    dragOverIndex,
    insertBeforeIndex,
    collapsedGroups,
    getStepInfo,
    onContextSelect,
    onContextDelete,
    onContextDuplicate,
    onContextToggleDisable,
    handleToggleSelection,
    handleToggleGroupCollapse,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  ]);

  return (
    <>
      <Stack direction="column" spacing={0.5} alignItems="stretch" justifyContent="flex-start">
        {/* Add new step button */}
        <Button
          variant="outlined"
          size="small"
          disabled={mutating || isCreating}
          onClick={handleCreateNewStep}
          startIcon={<AddCircleIcon />}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontSize: "13px",
            py: 0.75,
            borderStyle: "dashed",
            color: "text.secondary",
            borderColor: "rgba(0, 0, 0, 0.12)",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "rgba(22, 0, 234, 0.04)",
            },
          }}
        >
          Add step
        </Button>

        {/* Show insertion line at the top if inserting before first item */}
        {insertBeforeIndex === 0 && draggedContextId && (
          <Box
            sx={{
              height: "2px",
              backgroundColor: "primary.main",
              borderRadius: "1px",
              margin: "4px 0",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: "-4px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "primary.main",
              },
            }}
          />
        )}

        {ctxsList}

        {/* Show insertion line at the bottom if inserting after last item */}
        {insertBeforeIndex !== null &&
          insertBeforeIndex === (ctxs?.length || 0) &&
          draggedContextId && (
            <Box
              sx={{
                height: "2px",
                backgroundColor: "primary.main",
                borderRadius: "1px",
                margin: "4px 0",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: "-4px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                },
              }}
            />
          )}
      </Stack>

      <TestStepsFloatingActionBar
        selectedContexts={selectedContexts}
        onDelete={handleDeleteSelected}
        onDuplicate={handleDuplicateSelected}
        onToggleDisable={handleToggleDisableSelected}
      />
    </>
  );
}
